// TrackerDashboard.tsx

import React, { useEffect, useState } from 'react';
import { Select, DatePicker, Button, Card } from 'antd'; // Using AntD components
import {
  topMetricsConfig,
  bottomMetricsConfig,
  bdePerformanceConfig,
} from './config';
import { TopMetricCard, BottomMetricCard } from './MetricCard';
import { BdePerformanceItem } from './BdePerformanceItem';
import DrawerComponent from '../../../../../../components/Forms/Drawer';
import MetricDetailsTable from './MetricDetailsTable';
import dayjs, { Dayjs } from 'dayjs';
import { getData } from '../../../../../../actions/apiCall';
import { useDispatch, useSelector } from 'react-redux';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface TrackerDashboardProps {
  onFiltersChange?: (filters: { bdeId: string | null; startDate: dayjs.Dayjs | null; endDate: dayjs.Dayjs | null; period: string }) => void;
}

export const TrackerDashboard: React.FC<TrackerDashboardProps> = ({ onFiltersChange }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedBDE, setSelectedBDE] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('month');
  const [employeeOptions, setEmployeeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [trackerData, setTrackerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const { user, isGlobalAdmin } = useSelector((state: any) => state.app);
  const isAdmin = isGlobalAdmin || user?.designation === 'Admin';

  useEffect(() => {
    // Set default dates on mount
    const today = dayjs();
    setStartDate(today.startOf('month'));
    setEndDate(today.endOf('month'));

    // For non-admin, set current user ID automatically
    if (!isAdmin && user?._id) {
      setSelectedBDE(user._id);
    }

    // Only fetch employees if admin
    if (isAdmin) {
      getEmployees();
    }
  }, [isAdmin, user]);

  useEffect(() => {
    // For non-admin, ensure current user ID is set
    if (!isAdmin && user?._id && !selectedBDE) {
      setSelectedBDE(user._id);
      return;
    }

    // For admin: fetch data even if no employee selected (overall data)
    // For non-admin: require employee selection (which should be current user)
    if (startDate && endDate && (isAdmin || selectedBDE)) {
      fetchTrackerData();
    }

    // Notify parent component of filter changes
    if (onFiltersChange) {
      onFiltersChange({
        bdeId: selectedBDE,
        startDate,
        endDate,
        period: selectedPeriod
      });
    }
  }, [selectedBDE, startDate, endDate, selectedPeriod, isAdmin, user]);

  const getEmployees = async () => {
    // Only fetch employees if admin
    if (!isAdmin) return;

    const res = await dispatch(getData('/users'));

    if (res?.data) {
      const options = res.data.map((item: any) => ({
        value: item._id,
        label: `${item.firstName} ${item.lastName || ''}`.trim(),
      }));

      // Add "All" option for admins
      options.unshift({ value: 'all', label: 'All BDEs' });

      setEmployeeOptions(options);

      // Set default: "All" for admin if not already set
      if (options.length > 0 && !selectedBDE) {
        setSelectedBDE('all');
      }
    }
  };

  const fetchTrackerData = async () => {
    if (!startDate || !endDate) return;
    // For non-admin, require employee selection
    if (!isAdmin && !selectedBDE) return;

    try {
      setLoading(true);
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const bdeIdParam = selectedBDE === 'all' || !selectedBDE ? 'all' : selectedBDE;

      const res = await dispatch(
        getData(`/targets/bde/tracker?bdeId=${bdeIdParam}&startDate=${startDateStr}&endDate=${endDateStr}&period=${selectedPeriod}`)
      );

      if (res?.code === 200 && res?.data) {
        setTrackerData(res.data);
      }
    } catch (error) {
      console.error('Error fetching tracker data:', error);
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current: Dayjs) => {
    if (startDate) {
      return current.isBefore(startDate.startOf('day'));
    }
    return false;
  };

  const openDrawer = (title: string) => {
    setSelectedMetric(title);
    setDrawerVisible(true);
  };

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value);
    const today = dayjs();
    if (value === 'month') {
      setStartDate(today.startOf('month'));
      setEndDate(today.endOf('month'));
    } else if (value === 'quarter') {
      // Calculate quarter manually
      const quarter = Math.floor(today.month() / 3);
      const quarterStart = today.month(quarter * 3).startOf('month');
      const quarterEnd = today.month(quarter * 3 + 2).endOf('month');
      setStartDate(quarterStart);
      setEndDate(quarterEnd);
    }
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
    }
  };

  // Use API data if available, otherwise use config defaults
  // Always prefer API data when available
  const topMetrics = trackerData?.topMetrics || topMetricsConfig;
  const bottomMetrics = trackerData?.bottomMetrics || bottomMetricsConfig;
  const bdePerformance = trackerData?.bdePerformance || (trackerData?.isAllBDEs ? [] : bdePerformanceConfig);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 rounded-lg">
        <h1 className="text-xl font-bold text-gray-700">Tracker</h1>
        <div className="flex space-x-4 items-center">
          {isAdmin && (
            <Select
              value={selectedBDE}
              options={employeeOptions}
              style={{ width: 180 }}
              onChange={(value) => setSelectedBDE(value)}
              loading={loading}
              placeholder="Select BDE or All"
              allowClear={true}
              showSearch={true}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onClear={() => {
                setSelectedBDE('all');
              }}
            />
          )}
          <Select
            value={selectedPeriod}
            placeholder="Select Period"
            style={{ width: 150 }}
            onChange={handlePeriodChange}
          >
            <Option value="month">This Month</Option>
            <Option value="quarter">This Quarter</Option>
          </Select>
          <RangePicker
            style={{ width: 220 }}
            disabledDate={disabledDate}
            value={startDate && endDate ? [startDate, endDate] : null}
            onChange={handleDateRangeChange}
            onCalendarChange={(dates) => {
              if (dates && dates[0]) {
                setStartDate(dates[0]);
              }
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Loading tracker data...</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6">
              <div className="grid grid-cols-2 gap-6">
                {topMetrics.map((metric: any, index: number) => (
                  <TopMetricCard
                    key={metric.type}
                    data={metric}
                    index={index}
                    onClick={() => openDrawer(metric.title)}
                  />
                ))}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-6">
              <Card
                title={<h2 className="text-lg font-semibold text-gray-700">BDE Performance</h2>}
                className="rounded-lg"
                bodyStyle={{ padding: 0 }}
              >
                {bdePerformance.length > 0 ? (
                  bdePerformance.map((item: any, index: number) => (
                    <React.Fragment key={index}>
                      <BdePerformanceItem data={item} />
                      {index < bdePerformance.length - 1 && (
                        <div className="w-full border-b border-gray-200"></div>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No performance alerts for the selected period
                  </div>
                )}
              </Card>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-6 mt-6">
            {bottomMetrics.map((metric: any) => (
              <BottomMetricCard key={metric.type} data={metric} />
            ))}
          </div>
        </>
      )}
      <DrawerComponent
        visible={drawerVisible}
        handleCancel={() => setDrawerVisible(false)}
        title={selectedMetric}
        isView={true}
        loading={false}
        submitForm={() => { }}

      >
        <MetricDetailsTable title={selectedMetric} />
      </DrawerComponent>
    </div>
  );
};